import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Users,
  CalendarDays,
  Layers,
  FlaskConical,
  ReceiptText,
  BarChart3,
  Smartphone,
  CloudOff,
  RefreshCw,
  QrCode,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const MOBILE_APP_URL = '/downloads/agrovet-mobile.apk';

const MOBILE_POINTS = [
  { icon: CloudOff, text: 'Capture payroll tasks, milling, farm sales and prepayments fully offline' },
  { icon: RefreshCw, text: 'Automatic background sync the moment connectivity returns' },
  { icon: BarChart3, text: 'Company and cycle dashboards, read-only in the field' },
  { icon: QrCode, text: 'Provisioned in seconds with a QR code from your administrator' },
];

const FEATURES = [
  { icon: Layers, title: 'Estate Management', desc: 'Track blocks, operations, and yields across multiple estates.' },
  { icon: CalendarDays, title: 'Payroll & Cycles', desc: 'Automate worker payroll, deductions, and leave across cycles.' },
  { icon: Users, title: 'Personnel Records', desc: 'Manage employee profiles, contracts, and employment history.' },
  { icon: FlaskConical, title: 'CPO Inventory', desc: 'Track raw, bulk, and sterilised oil with FIFO cost lots.' },
  { icon: ReceiptText, title: 'Sales & Expenses', desc: 'Record farm, bulk, and retail sales with COGS stamping.' },
  { icon: BarChart3, title: 'Reporting', desc: 'Operational and financial reports across all modules.' },
];

const HIGHLIGHTS = [
  'Real-time estate and payroll data',
  'Multi-estate support',
  'Role-based access control',
  'CPO cost and margin tracking',
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-[#060f09] text-white">
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0b2818]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#22c55e]">
              <span className="text-sm font-bold text-white">AV</span>
            </div>
            <span className="font-semibold tracking-tight">Agrovet ERP</span>
          </div>
          <Link to="/sign-in">
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Sign in
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative flex min-h-[100svh] flex-col items-center justify-center px-6 text-center"
        style={{
          backgroundImage: "url('/palms.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#071b0f]/75" />
        <div className="relative z-10 max-w-3xl">
          <h1 className="text-5xl font-bold leading-tight tracking-tight sm:text-6xl">
            Sow.{' '}
            <span className="text-[#22c55e]">Nurture.</span>{' '}
            Thrive.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-white/80">
            End-to-end management for palm oil estates — payroll, CPO inventory,
            sales, and reporting in one platform.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            {HIGHLIGHTS.map((h) => (
              <span key={h} className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#22c55e]" />
                {h}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/sign-in">
              <Button size="lg" className="bg-[#22c55e] text-black hover:bg-[#16a34a]">
                Access Platform
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href={MOBILE_APP_URL} download>
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Smartphone className="mr-2 h-4 w-4" />
                Get the Android App
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-12 text-center text-3xl font-bold text-[#0b2818]">
            Everything your estate needs
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="rounded-xl border border-gray-100 p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#f4faf5]">
                  <Icon className="h-5 w-5 text-[#22c55e]" />
                </div>
                <h3 className="mb-1 font-semibold text-[#0b2818]">{title}</h3>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile App */}
      <section className="bg-[#f4faf5] px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold text-[#0b2818]">Take Agrovet ERP to the field</h2>
              <p className="mt-3 leading-relaxed text-gray-600">
                Agrovet Mobile is the companion app for field data entry — built for estates
                where connectivity comes and goes.
              </p>
              <ul className="mt-6 space-y-3">
                {MOBILE_POINTS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                      <Icon className="h-4 w-4 text-[#22c55e]" />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <a href={MOBILE_APP_URL} download>
                  <Button size="lg" className="bg-[#22c55e] text-black hover:bg-[#16a34a]">
                    <Smartphone className="mr-2 h-4 w-4" />
                    Download for Android
                  </Button>
                </a>
                <p className="mt-3 text-xs text-gray-400">
                  Android 8.0 or later · Install the APK, then scan the provisioning QR code from
                  your administrator to activate the device.
                </p>
              </div>
            </div>
            <div className="hidden justify-center lg:flex">
              <div className="flex h-72 w-72 items-center justify-center rounded-3xl bg-[#0b2818]">
                <Smartphone className="h-28 w-28 text-[#22c55e]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0b2818] px-6 py-16 text-center">
        <h2 className="text-3xl font-bold">Ready to get started?</h2>
        <p className="mt-3 text-white/70">Sign in to access your estate dashboard.</p>
        <div className="mt-8">
          <Link to="/sign-in">
            <Button size="lg" className="bg-[#22c55e] text-black hover:bg-[#16a34a]">
              Sign in to Agrovet ERP
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#060f09] px-6 py-8 text-center text-sm text-white/40">
        &copy; {new Date().getFullYear()} Agrovet ERP. All rights reserved.
      </footer>
    </div>
  );
}
